import {
  useState,
  useEffect,
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useRef,
} from 'react';
import { v4 as uuid } from 'uuid';

import { getAnalyzerType } from '../../utils/analyzerTypes';
import { createLicense } from '../analyzers/analyzerSlice';

import {
  clearMessages,
  selectMessage,
  testConnection,
} from '../spectrasWebSocketServer/spectrasWebSocketServerSlice';

import {
  Grid,
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';

import GreenButton from '../../components/GreenButton';

import Alert from '@mui/material/Alert';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { InstrumentForm } from './types';
import { getSwitchType } from 'utils/switchTypes';

import { Switch, Analyzer } from 'types';

import { useCreateAnalyzer } from 'features/analyzers/api/createAnalyzer';
import { useUpdateAnalyzer } from 'features/analyzers/api/updateAnalyzer';
import { useCreateSwitch } from 'features/switches/api/createSwitch';
import { useUpdateSwitch } from 'features/switches/api/updateSwitch';
import { InstrumentType } from '../analyzerDataSettings/types';

const defaultInstrumentState: InstrumentForm = {
  name: '',
  ip: '',
  port: '5025',
  type: 0,
  number_of_ports: '4',
  memo: '',
  enabled: false,
  status: false,
};

interface Props {
  open: boolean;
  handleInstrumentTypeChanged: (e: SelectChangeEvent) => void;
  handleClose: () => void;
  setSnackBar: Dispatch<
    SetStateAction<{
      severity: 'success' | 'error';
      open: boolean;
      message: string;
    }>
  >;
  selectedInstrument: Analyzer | Switch | undefined;
  selectedInstrumentType: number;
}

const ConnectInstrumentDialog = ({
  open,
  handleClose,
  handleInstrumentTypeChanged,
  selectedInstrument,
  selectedInstrumentType,
  setSnackBar,
}: Props) => {
  const dispatch = useAppDispatch();
  const message = useAppSelector(selectMessage);

  const [instrument, setInstrument] = useState<InstrumentForm>(
    defaultInstrumentState
  );

  const [unitTested, setUnitTested] = useState(false);
  const [error, setError] = useState<null | string>(null);
  const [successMessage, setSuccessMessage] = useState('');
  const timeout = useRef<NodeJS.Timeout | undefined>();
  const [formChanged, setFormChanged] = useState(false);

  const createAnalyzerMutation = useCreateAnalyzer({});
  const updateAnalyzerMutation = useUpdateAnalyzer({});
  const createSwitchMutation = useCreateSwitch({});
  const updateSwitchMutation = useUpdateSwitch({});

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent
  ) => {
    const { name, value } = e.target;
    setInstrument({
      ...instrument,
      [name]: value,
    });
    setFormChanged(true);
    setUnitTested(false);
  };

  const setInstrumentPort = () => {
    if (
      instrument.type === null ||
      !(
        instrument.type === 0 ||
        instrument.type === 1 ||
        instrument.type === 3 ||
        instrument.type === 4
      )
    )
      return;
      
    let instrumentPort: string;
    if (selectedInstrumentType === InstrumentType.ANALYZER) {
      instrumentPort = getAnalyzerType(instrument.type).DEFAULT_PORT;
    } else {
      instrumentPort = getSwitchType(instrument.type).DEFAULT_PORT;
    }
    setInstrument((prevState) => ({
      ...prevState,
      port: instrumentPort,
    }));
  };

  useEffect(() => {
    setSuccessMessage('');
    setUnitTested(selectedInstrumentType !== InstrumentType.ANALYZER);
    if (!selectedInstrument) {
      setInstrument(defaultInstrumentState);
      setInstrumentPort();
      dispatch(clearMessages());
      return;
    }
    setInstrument({
      ...selectedInstrument,
      enabled: !!selectedInstrument.enabled,
    });
    dispatch(clearMessages());
    //eslint-disable-next-line
  }, [open]);

  useEffect(() => {
    if (instrument?.type === undefined) return;
    setInstrumentPort();
    //eslint-disable-next-line
  }, [instrument?.type, selectedInstrumentType]);

  useEffect(() => {
    if (message[0]?.type === 'test_connection') {
      //Clear the timeout
      clearTimeout(timeout.current);
      if (
        instrument.type === null ||
        !(
          instrument.type === 0 ||
          instrument.type === 1 ||
          instrument.type === 3 ||
          instrument.type === 4 ||
          instrument.type === 5
        )
      )
        return;
      const analyzerResponse = getAnalyzerType(
        instrument.type
      ).parseIdnResponse(message[0].message);

      if (analyzerResponse) {
        setInstrument((prevState) => ({
          ...prevState,
          serial: analyzerResponse.serial,
        }));
        setUnitTested(true);
        setSuccessMessage('Instrument Connected!');
      }
    }
  }, [message, instrument.type]);

  const releaseInstrument = async () => {
    if (selectedInstrument?.id) {
      const updatedInstrument = { id: selectedInstrument?.id, enabled: false };

      if (selectedInstrumentType === InstrumentType.ANALYZER) {
        await updateAnalyzerMutation.mutateAsync({
          data: updatedInstrument,
          analyzerId: selectedInstrument?.id,
        });
      } else {
        await updateSwitchMutation.mutateAsync({
          data: updatedInstrument,
          switchId: selectedInstrument?.id,
        });
      }
    }
  };

  const testClicked = () => {
    setFormChanged(false);

    // Clear any existing timeout
    clearTimeout(timeout.current);

    // Start a new timeout
    timeout.current = setTimeout(() => {
      // Timeout triggered, indicating no response
      setSnackBar({
        open: true,
        severity: 'error',
        message: 'Connection Error: Check IP or Server Status',
      });
      releaseInstrument();
    }, 3000); // Adjust the timeout duration as needed

    dispatch(
      testConnection({
        instrument: {
          ...instrument,
          instrument_category: selectedInstrumentType,
        },
      })
    );
  };

  const addAnalyzer = async () => {
    if (selectedInstrument) {
      const id = instrument.id;
      if (id === undefined) return;
      const updatedAnalyzer = {
        ...instrument,
        id,
        port: Number(instrument.port),
        number_of_ports: Number(instrument.number_of_ports),
        ip: instrument.ip ? instrument.ip : undefined,
        type: instrument.type !== null ? instrument.type : undefined,
        memo: instrument.memo ? instrument.memo : undefined,
        enabled: instrument.enabled !== null ? instrument.enabled : undefined,
      };
      try {
        await updateAnalyzerMutation.mutateAsync({
          data: updatedAnalyzer,
          analyzerId: id,
        });
        setError(null);
        handleClose();
      } catch (e) {
        setError('No license found.');
      }
    } else {
      const { serial, ip, type } = instrument;
      if (
        serial === undefined ||
        ip === undefined ||
        ip === null ||
        type === undefined ||
        type === null
      )
        return;

      const newAnalyzer = {
        ...instrument,
        serial,
        port: Number(instrument.port),
        number_of_ports: Number(instrument.number_of_ports),
        ip,
        type,
        enabled: !!instrument.enabled,
        status: !!instrument.status,
        memo: instrument.memo ? instrument.memo : undefined,
      };
      try {
        await createAnalyzerMutation.mutateAsync({
          data: newAnalyzer,
        });
        setError(null);
        handleClose();
      } catch (e) {
        setError('No license found.');
      }
    }
  };

  const addSwitch = async () => {
    if (selectedInstrument) {
      try {
        const id = instrument.id;
        if (id === undefined) return;
        const updatedSwitch = {
          ...instrument,
          id,
          port: Number(instrument.port),
          number_of_ports: Number(instrument.number_of_ports),
        };
        await updateSwitchMutation.mutateAsync({
          data: updatedSwitch,
          switchId: id,
        });
      } catch (e) {
        setSnackBar({
          severity: 'error',
          open: true,
          message: 'Switch limit exceeded.',
        });
      }
    } else {
      try {
        const newSwitch = {
          ...instrument,
          serial: uuid(),
          port: Number(instrument.port),
          number_of_ports: Number(instrument.number_of_ports),
          number_of_outputs: Number(instrument.number_of_outputs),
        };
        await createSwitchMutation.mutateAsync({
          data: newSwitch,
        });
      } catch (e) {
        setSnackBar({
          severity: 'error',
          open: true,
          message: 'Switch limit exceeded.',
        });
      }
    }
    handleClose();
  };

  const addAnalyzerClicked = async () => {
    setSuccessMessage('');
    if (selectedInstrumentType === InstrumentType.ANALYZER) {
      addAnalyzer();
    } else {
      addSwitch();
    }
  };

  const uploadFile = async (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (e) => {
      const text = e.target?.result;
      if (!text || typeof text !== 'string') return;
      await dispatch(createLicense(JSON.parse(text)));
      setSuccessMessage('License uploaded successfully');
      addAnalyzer();
    };
    e.target.value = '';
    reader.readAsText(file);
  };

  const instrumentTypesMenuItems = () => {
    if (selectedInstrumentType === InstrumentType.ANALYZER) {
      return [
        <MenuItem key={0} value={0}>
          LPT-3000RX4
        </MenuItem>,
        <MenuItem key={1} value={1}>
          LPT-3000R
        </MenuItem>,
        <MenuItem key={3} value={3}>
          Anristu MS27103A
        </MenuItem>,
        <MenuItem key={4} value={4}>
          SED Decimator
        </MenuItem>,
        <MenuItem key={5} value={5}>
          Rohde & Schwarz FPS7
        </MenuItem>,
      ];
    } else {
      return [
        <MenuItem key={0} value={0}>
          ETL LS 23227
        </MenuItem>,
        <MenuItem key={1} value={1}>
          LPT-SX5000
        </MenuItem>,
        <MenuItem key={3} value={3}>
          Quintech Xtreme 80
        </MenuItem>
      ];
    }
  };

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>Instrument</DialogTitle>
      <DialogContent>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            {error && <Alert severity='error'>{error}</Alert>}
            {successMessage && (
              <Alert severity='success'>{successMessage}</Alert>
            )}
          </Grid>
          {!error && (
            <>
              <Grid item xs={12}>
                <TextField
                  required
                  id='name'
                  name='name'
                  label='Unit Name'
                  fullWidth
                  onChange={handleChange}
                  value={instrument.name}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  id='ip'
                  name='ip'
                  label='Unit IP'
                  fullWidth
                  onChange={handleChange}
                  value={instrument.ip}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  id='port'
                  name='port'
                  label='Unit Port'
                  fullWidth
                  onChange={handleChange}
                  value={instrument?.port}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id='device-select-label'>Device</InputLabel>
                  <Select
                    labelId='device-select-label'
                    id='device'
                    name='device'
                    label='Device'
                    value={'' + selectedInstrumentType}
                    onChange={handleInstrumentTypeChanged}
                  >
                    <MenuItem key={0} value={0}>
                      Spectrum Analyzer
                    </MenuItem>
                    <MenuItem key={1} value={1}>
                      Switch
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id='unit-type-select-label'>Unit Type</InputLabel>
                  <Select
                    labelId='unit-type-select-label'
                    id='type'
                    name='type'
                    label='Unit Type'
                    onChange={handleChange}
                    value={'' + instrument.type}
                  >
                    {instrumentTypesMenuItems()}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id='number-of-ports-select-label'>
                    Number of Input Ports
                  </InputLabel>
                  <Select
                    labelId='number-of-ports-select-label'
                    id='number_of_ports'
                    label='Number of Input Ports'
                    name='number_of_ports'
                    onChange={handleChange}
                    value={'' + instrument.number_of_ports}
                  >
                    <MenuItem key={1} value={1}>
                      1
                    </MenuItem>
                    <MenuItem key={4} value={4}>
                      4
                    </MenuItem>
                    <MenuItem key={8} value={8}>
                      8
                    </MenuItem>
                    <MenuItem key={12} value={12}>
                      12
                    </MenuItem>
                    <MenuItem key={16} value={16}>
                      16
                    </MenuItem>
                    {selectedInstrumentType === InstrumentType.SWITCH && (
                        <MenuItem key={32} value={32}>
                          32
                        </MenuItem>
                      )}
                      {selectedInstrumentType === InstrumentType.SWITCH && (
                        <MenuItem key={40} value={40}>
                          40
                        </MenuItem>
                      )}
                      {selectedInstrumentType === InstrumentType.SWITCH && (
                        <MenuItem key={64} value={64}>
                          64
                        </MenuItem>
                      )}
                  </Select>
                </FormControl>
              </Grid>
              { (selectedInstrumentType === InstrumentType.SWITCH) && (
            <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id='number-of-outputs-label'>
                    Number of Output Ports
                  </InputLabel>
                  <Select
                    labelId='number-of-outputs-select-label'
                    id='number_of_outputs'
                    label='Number of Output Ports'
                    name='number_of_outputs'
                    onChange={handleChange}
                    value={'' + instrument.number_of_outputs}          
                  >
                    <MenuItem key={1} value={1}>
                      1
                    </MenuItem>
                    <MenuItem key={4} value={4}>
                      4
                    </MenuItem>
                    <MenuItem key={8} value={8}>
                      8
                    </MenuItem>
                    <MenuItem key={12} value={12}>
                      12
                    </MenuItem>
                    <MenuItem key={16} value={16}>
                      16
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              )}
              <Grid item xs={12}>
                <TextField
                  id='memo'
                  name='memo'
                  label='Memo'
                  fullWidth
                  onChange={handleChange}
                  value={instrument.memo}
                />
              </Grid>
            </>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Grid container spacing={1}>
          {error && (
            <>
              <Grid
                container
                item
                xs={12}
                alignItems='center'
                justifyContent='center'
              >
                <input
                  style={{ display: 'none' }}
                  id='contained-button-file'
                  type='file'
                  accept='.lic'
                  onChange={uploadFile}
                />
                <label htmlFor='contained-button-file'>
                  <Button variant='contained' color='primary' component='span'>
                    Upload License
                  </Button>
                </label>
              </Grid>
            </>
          )}
          {!error && (
            <>
              <Grid item xs={4} />
              <Grid item xs={2}>
                <Button
                  onClick={handleClose}
                  variant='contained'
                  color='secondary'
                  fullWidth
                >
                  Cancel
                </Button>
              </Grid>
              <Grid item xs={2}>
                <Button
                  onClick={testClicked}
                  variant='contained'
                  color='primary'
                  fullWidth
                  disabled={selectedInstrumentType !== InstrumentType.ANALYZER}
                >
                  Test
                </Button>
              </Grid>
              <Grid item xs={4}>
                <GreenButton
                  onClick={addAnalyzerClicked}
                  name={
                    selectedInstrument ? 'Update Instrument' : 'Add Instrument'
                  }
                  disabled={
                    (selectedInstrumentType !== InstrumentType.ANALYZER
                      ? unitTested
                      : !unitTested) ||
                    !instrument.name ||
                    !instrument.ip ||
                    (selectedInstrumentType !== InstrumentType.ANALYZER
                      ? false
                      : formChanged)
                  }
                >
                  {selectedInstrument ? 'Update Instrument' : 'Add Instrument'}
                </GreenButton>
              </Grid>
            </>
          )}
        </Grid>
      </DialogActions>
    </Dialog>
  );
};

export default ConnectInstrumentDialog;
